import axiosInstance from './axiosInstance';
import store from '@state/store';
import { GRAPHQL_ENDPOINT, PUBLIC_GRAPHQL_ENDPOINT } from './config';
import { APP_ID, getCurrentAppId } from './currentAppId';

export { APP_ID, getCurrentAppId };

const getWindowLocation = () => {
  if (typeof window === 'undefined' || !window.location) {
    return undefined;
  }

  return window.location;
};

export const privateGraphQlUrl = GRAPHQL_ENDPOINT;
export const publicGraphQlUrl = PUBLIC_GRAPHQL_ENDPOINT;

const GRAPHQL_MODE_PUBLIC = 'public';
const GRAPHQL_MODE_PRIVATE = 'private';

const LOCAL_HOSTNAMES = new Set([
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
]);

const LOCAL_HOSTNAME_SUFFIXES = ['.local', '.localhost', '.test'];

const PRIVATE_IPV4_PATTERN = /^(10\.|192\.168\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/;

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const parseGraphQlMode = () => {
  const mode = normalizeString(process.env.REACT_APP_GRAPHQL_ENDPOINT_MODE);

  if (mode === GRAPHQL_MODE_PUBLIC || mode === GRAPHQL_MODE_PRIVATE) {
    return mode;
  }

  return 'auto';
};

const parseAllowedOrigins = (value) =>
  (value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const resolveDefaultAppId = () => getCurrentAppId();

const isLocalHostname = (hostname) => {
  if (!hostname) {
    return false;
  }

  const normalized = hostname.toLowerCase();

  if (LOCAL_HOSTNAMES.has(normalized)) {
    return true;
  }

  if (PRIVATE_IPV4_PATTERN.test(normalized)) {
    return true;
  }

  return LOCAL_HOSTNAME_SUFFIXES.some((suffix) => normalized.endsWith(suffix));
};

const shouldUsePublicGraphQl = () => {
  if (!privateGraphQlUrl && publicGraphQlUrl) {
    return true;
  }

  const mode = parseGraphQlMode();

  if (mode === GRAPHQL_MODE_PUBLIC) {
    return true;
  }

  if (mode === GRAPHQL_MODE_PRIVATE) {
    return false;
  }

  const location = getWindowLocation();

  if (!location) {
    return false;
  }

  const { origin, protocol, hostname } = location;

  if (protocol === 'file:') {
    return true;
  }

  if (isLocalHostname(hostname)) {
    return true;
  }

  const allowedOrigins = parseAllowedOrigins(
    process.env.REACT_APP_PRIVATE_GRAPHQL_ALLOWED_ORIGINS
  );

  if (allowedOrigins.length > 0) {
    return !allowedOrigins.includes(origin);
  }

  return false;
};

export const graphQlUrl = shouldUsePublicGraphQl()
  ? publicGraphQlUrl || privateGraphQlUrl
  : privateGraphQlUrl || publicGraphQlUrl;

export const getAuthHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

const isObjectLike = (value) => value !== null && typeof value === 'object';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const safeJsonParse = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const safeGetState = () => {
  try {
    return typeof store.getState === 'function' ? store.getState() : undefined;
  } catch (error) {
    return undefined;
  }
};

const getStoredUser = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem('user');
    return raw ? safeJsonParse(raw) : null;
  } catch (error) {
    return null;
  }
};

const getStoredAccessToken = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  try {
    const token = window.localStorage.getItem('access');
    return token && token.trim() !== '' ? token : null;
  } catch (error) {
    return null;
  }
};

const resolveAccessToken = () => {
  const state = safeGetState();

  const tokenCandidates = [
    state?.auth?.access,
    state?.auth?.token,
    state?.auth?.idToken,
  ];

  for (const candidate of tokenCandidates) {
    if (hasValue(candidate)) {
      return typeof candidate === 'string' ? candidate : String(candidate);
    }
  }

  const storedToken = getStoredAccessToken();
  if (hasValue(storedToken)) {
    return storedToken;
  }

  return null;
};

const enqueueNestedCandidates = (queue, candidate) => {
  const nestedKeys = [
    'user',
    'profile',
    'data',
    'payload',
    'result',
    'context',
    'meta',
    'details',
    'information',
  ];

  nestedKeys.forEach((key) => {
    const nested = candidate[key];
    if (!nested) {
      return;
    }

    if (Array.isArray(nested)) {
      nested.forEach((item) => {
        if (isObjectLike(item)) {
          queue.push(item);
        }
      });
    } else if (isObjectLike(nested)) {
      queue.push(nested);
    }
  });

  if (isObjectLike(candidate.app)) {
    queue.push(candidate.app);
  }
};

const findFirstValue = (sources, extractor) => {
  const queue = sources.filter((item) => isObjectLike(item));
  const visited = new WeakSet();

  while (queue.length > 0) {
    const current = queue.shift();

    if (!current || visited.has(current)) {
      continue;
    }

    visited.add(current);

    const extracted = extractor(current);
    if (hasValue(extracted)) {
      return extracted;
    }

    enqueueNestedCandidates(queue, current);
  }

  return undefined;
};

const extractUserId = (candidate) => {
  if (!isObjectLike(candidate)) {
    return undefined;
  }

  const directKeys = [
    'userId',
    'user_id',
    'cognito_user_id',
    'cognitoUserId',
    'cognitoSub',
    'sub',
  ];

  for (const key of directKeys) {
    const value = candidate[key];
    if (hasValue(value)) {
      return value;
    }
  }

  if (
    hasValue(candidate.id) &&
    (hasValue(candidate.email) ||
      hasValue(candidate.username) ||
      hasValue(candidate.firstName) ||
      hasValue(candidate.lastName))
  ) {
    return candidate.id;
  }

  return undefined;
};

const extractAppId = (candidate) => {
  if (!isObjectLike(candidate)) {
    return undefined;
  }

  const directKeys = ['appId', 'app_id', 'appID', 'applicationId'];

  for (const key of directKeys) {
    const value = candidate[key];
    if (hasValue(value)) {
      return value;
    }
  }

  if (isObjectLike(candidate.settings) && hasValue(candidate.settings?.appId)) {
    return candidate.settings.appId;
  }

  return undefined;
};

export const resolveGraphQlMeta = (meta = {}) => {
  const normalizedMeta = { ...(meta || {}) };
  const state = safeGetState();

  const sources = [normalizedMeta];

  if (state?.user) {
    sources.push(state.user);
  }

  if (state?.auth) {
    sources.push(state.auth);

    if (state.auth.user) {
      sources.push(state.auth.user);
    }
  }

  const storedUser = getStoredUser();
  if (storedUser) {
    sources.push(storedUser);
  }

  const resolvedUserId =
    normalizedMeta.userId || findFirstValue(sources, extractUserId);
  const resolvedAppId =
    normalizedMeta.appId || getCurrentAppId() || findFirstValue(sources, extractAppId) || resolveDefaultAppId();

  if (!normalizedMeta.userId && hasValue(resolvedUserId)) {
    normalizedMeta.userId = resolvedUserId;
  }

  if (!normalizedMeta.appId && hasValue(resolvedAppId)) {
    normalizedMeta.appId = resolvedAppId;
  }

  return Object.entries(normalizedMeta).reduce((acc, [key, value]) => {
    if (hasValue(value)) {
      acc[key] = typeof value === 'string' ? value : String(value);
    }

    return acc;
  }, {});
};

const buildGraphQlPayload = (query, variables, meta) => {
  const payload = { query };

  if (variables && Object.keys(variables).length > 0) {
    payload.variables = variables;
  }

  const resolvedMeta = resolveGraphQlMeta(meta);

  if (Object.keys(resolvedMeta).length > 0) {
    Object.assign(payload, resolvedMeta);
  }

  return payload;
};

export const executePrivateGraphQl = (query, variables = {}, config = {}, meta) => {
  const defaultHeaders = { 'Content-Type': 'application/json' };
  const mergedHeaders = {
    ...defaultHeaders,
    ...(config.headers || {}),
  };

  const authToken = resolveAccessToken();

  if (authToken && !mergedHeaders.Authorization) {
    mergedHeaders.Authorization = `Bearer ${authToken}`;
  }

  const requestConfig = {
    ...config,
    headers: mergedHeaders,
  };
  const operationIsMutation =
    typeof query === 'string' && /\bmutation\b/i.test(query);
  const requestUrl = operationIsMutation
    ? privateGraphQlUrl || graphQlUrl
    : requestConfig.url || graphQlUrl;

  return axiosInstance.request({
    ...requestConfig,
    url: requestUrl,
    method: 'post',
    data: buildGraphQlPayload(query, variables, meta),
  });
};

export const parseSSE = (dataChunk) => {
  const spliter = 'event: message';
  const chunks = dataChunk.split(spliter);
  const lastChunk = chunks[chunks.length - 2];
  const dataPart = lastChunk?.split('data: ')[1];

  if (dataPart) {
    try {
      const jsonData = JSON.parse(dataPart.trim());
      return jsonData;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return null;
    }
  }

  console.error('No data found in the input');
  return null;
};
